import React, { useCallback, useContext, useState } from "react";
import { WidgetContext } from "@pages/Dashboard/context";
import { useTranslation } from "react-i18next";

import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Slider,
  SliderProps,
  Switch,
  TextField,
} from "@mui/material";
import Stack from "@mui/material/Stack";
import { useLocalStorage } from "@services/common/hooks/useLocalStorage";
import { useDebouncedState } from "@common/hooks";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import AdvancedCookiesDialog from "../CookiesManager/AdvancedCookiesDialog";
import { AuthenticatedUser, User } from "@services/user/types";
import { updateUser } from "@services/user";
import { useErrorNotification } from "@common/components/Notification";
import Select from "@common/components/Select";
import { Priority } from "@services/common/types";
import MenuItem from "@common/components/MenuItem";
import AutoComplete from "@pages/Configuration/DeviceCatalog/FilterBar/AutoComplete";
import { useGlobalListPreferences } from "@common/components/GlobalPreferences/hooks";
import { GlobalListPageSizeField } from "./Field/GlobalListPageSizeField";
import { ticketsPriorityFilter } from "@pages/Dashboard/WidgetEdit/FilterPanel/constants";
import {
  MAX_PAGE_SIZE,
  MIN_PAGE_SIZE,
} from "@common/components/GlobalPreferences/defaults";

export const USER_SETTINGS_STORAGE_KEY = "user_settings";
const CAROUSEL_SPEED_LIMITS = {
  min: 3,
  max: 60,
};
export type UserSettings = {
  isCarouselActive: boolean;
  /**
   * @param carouselSpeed
   * @range Number between 3 and 60
   *
   * Stored in seconds to simplify the slider usage
   *
   * Multiply by 1e3 when the ms equivalent is needed
   */
  carouselSpeed: number;
  hideSimpleAssets: boolean;
  showSatelliteView: boolean;
  areAnalyticsActive: boolean | undefined;
  hideTicketPopup: boolean;
  ticketFilterByPriority: Priority[];
  popupLocation: PopupLocationKeys;
};
const DEFAULT_USER_SETTINGS: UserSettings = {
  isCarouselActive: true,
  carouselSpeed: 5,
  hideSimpleAssets: false,
  showSatelliteView: false,
  areAnalyticsActive: undefined,
  hideTicketPopup: false,
  ticketFilterByPriority: [],
  popupLocation: "Bottom Left Corner",
};

export type PopupLocationKeys =
  | "Bottom Center"
  | "Bottom Left Corner"
  | "Bottom Right Corner"
  | "Top Center"
  | "Top Left Corner"
  | "Top Right Corner";

export const popupLocationValues: PopupLocationKeys[] = [
  "Bottom Center",
  "Bottom Left Corner",
  "Bottom Right Corner",
  "Top Center",
  "Top Left Corner",
  "Top Right Corner",
];

export const useUserSettings = (): [
  value: UserSettings,
  setter: (value: UserSettings) => void
] => {
  const [user] = useAuthenticatedUser();
  const user_specific_key = `${USER_SETTINGS_STORAGE_KEY}_${user?.id}`;
  const [userSettings, setUserSettings] =
    useLocalStorage<UserSettings>(user_specific_key);

  // Future-proofing by checking each value stored one by one to take into account that we will be adding
  // more keys in the future, which would result in those specific keys being undefined, while the object is defined.
  const activeSettings = (
    Object.entries(DEFAULT_USER_SETTINGS) as [
      key: keyof UserSettings,
      value: UserSettings[keyof UserSettings]
    ][]
  ).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: userSettings?.[key] ?? value,
    }),
    {} as UserSettings
  );

  return [activeSettings, setUserSettings];
};

export const useUserSetting = <K extends keyof UserSettings>(
  setting: K
): [value: UserSettings[K], setter: (value: UserSettings[K]) => void] => {
  const [userSettings, setUserSettings] = useUserSettings();
  const setSetting = useCallback(
    (value: UserSettings[K]) =>
      setUserSettings({
        ...userSettings,
        [setting]: value,
      }),
    [userSettings, setting]
  );

  return [userSettings[setting], setSetting];
};

// Kind of required as the slider sends a LOT of events
const DebouncedNumberSlider = (
  props: Omit<SliderProps, "onChange" | "value"> & {
    value: number;
    onChange: (value: number) => void;
  }
) => {
  const [value, setValue] = useDebouncedState(props.value, props.onChange);

  return (
    <Slider
      {...props}
      value={value}
      onChange={(_, value) => setValue(Array.isArray(value) ? value[0] : value)}
    />
  );
};

const SettingsContent = () => {
  const { dispatch } = useContext(WidgetContext);
  const [user, setUser] = useAuthenticatedUser();
  const [userSettings, setUserSettings] = useUserSettings();
  const {
    isCarouselActive,
    carouselSpeed,
    hideSimpleAssets,
    showSatelliteView,
    areAnalyticsActive,
    hideTicketPopup,
    ticketFilterByPriority,
    popupLocation,
  } = userSettings;
  const [showAdvancedCookies, setShowAdvancedCookies] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const setUserSetting = useCallback(
    <K extends keyof UserSettings>(key: K, value: UserSettings[K]) =>
      setUserSettings({
        ...userSettings,
        [key]: value,
      }),
    [userSettings]
  );
  const { t } = useTranslation();
  const [{ pageSize, bulkImportPageSize }, setGlobalListPreferences] =
    useGlobalListPreferences();
  const dispatchError = useErrorNotification();

  const handleCarouselSwitch = (e: boolean) => {
    setUserSetting("isCarouselActive", e);
    if (e) dispatch("enableCarousel");
    else dispatch("disableCarousel");
  };

  return (
    <Stack
      sx={{
        gap: 1,
        height: "100%",
        overflowY: "auto"
      }}>
      <AdvancedCookiesDialog
        open={showAdvancedCookies}
        onClose={setShowAdvancedCookies}
      />
      <FormControl component="fieldset">
        <FormLabel component="legend">{t("UI Behaviors")}</FormLabel>
        <FormControlLabel
          sx={{ width: "fit-content", marginTop: 1 }}
          control={
            <Switch
              checked={isCarouselActive === true}
              onChange={(_, checked) => handleCarouselSwitch(checked)}
            />
          }
          label={"Carousel"}
          labelPlacement="start"
        />
        <FormControlLabel
          sx={{
            width: 2 / 3,
            marginTop: 1,
            "& > .MuiFormControlLabel-label": { whiteSpace: "nowrap" },
          }}
          disabled={!isCarouselActive}
          control={
            <DebouncedNumberSlider
              valueLabelDisplay="auto"
              size="small"
              {...CAROUSEL_SPEED_LIMITS}
              value={
                CAROUSEL_SPEED_LIMITS.max +
                CAROUSEL_SPEED_LIMITS.min -
                carouselSpeed
              }
              valueLabelFormat={(v) =>
                CAROUSEL_SPEED_LIMITS.max + CAROUSEL_SPEED_LIMITS.min - v
              }
              onChange={(number) => {
                setUserSetting(
                  "carouselSpeed",
                  CAROUSEL_SPEED_LIMITS.max + CAROUSEL_SPEED_LIMITS.min - number
                );
              }}
            />
          }
          label={"Carousel Speed"}
          labelPlacement="start"
        />
      </FormControl>
      <FormControl component="fieldset">
        <FormLabel component="legend">{t("Asset Management")}</FormLabel>
        <FormControlLabel
          sx={{ width: "fit-content", marginTop: 1 }}
          control={
            <Switch
              checked={hideSimpleAssets === true}
              onChange={(_, checked) => {
                setUserSetting("hideSimpleAssets", checked);
              }}
            />
          }
          label={"Hide Simple Assets"}
          labelPlacement="start"
        />
      </FormControl>
      <FormControl component="fieldset">
        <FormLabel component="legend">{t("Default Map View")}</FormLabel>
        <FormControlLabel
          sx={{ width: "fit-content", marginTop: 1 }}
          control={
            <Switch
              checked={showSatelliteView === true}
              onChange={(_, checked) => {
                setUserSetting("showSatelliteView", checked);
              }}
            />
          }
          label={"Satellite"}
          labelPlacement="start"
        />
      </FormControl>
      <FormControl component="fieldset">
        <FormLabel component="legend">
          {t("Cookies")}
          <Button
            onClick={() => setShowAdvancedCookies(true)}
            sx={{
              py: 0,
              textDecoration: "underline",
              ":hover": { textDecoration: "underline" },
            }}
          >
            {t("View Cookies").toLocaleLowerCase()}
          </Button>
        </FormLabel>
        <FormControlLabel
          sx={{ width: "fit-content", marginTop: 1 }}
          control={
            <Switch
              checked={areAnalyticsActive === true}
              onChange={(_, checked) => {
                setUserSetting("areAnalyticsActive", checked);
              }}
            />
          }
          label={"Analytic Cookies"}
          labelPlacement="start"
        />
      </FormControl>
      <FormControl>
        <FormLabel component="legend">{t("System of Measurement")}</FormLabel>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            gap: 1,
            mt: 1.5,
            ml: 3
          }}>
          {t("Metric (kg, km)")}
          <Switch
            disabled={loading}
            checked={user?.details.useMetricSystem}
            onChange={async (_, checked) => {
              try {
                setLoading(true);
                let tmp = {
                  ...user,
                  details: { ...user?.details, useMetricSystem: checked },
                };
                await updateUser(tmp as User, user?.id);
                setUser(tmp as AuthenticatedUser);
              } catch (error) {
                dispatchError(error);
              } finally {
                setLoading(false);
              }
            }}
          />
        </Stack>
      </FormControl>
      <FormControl component="fieldset">
        <FormLabel component="legend">{t("New Ticket Popup")}</FormLabel>
        <FormControlLabel
          sx={{ width: "fit-content", marginTop: 1 }}
          control={
            <Switch
              checked={hideTicketPopup === true}
              onChange={(_, checked) => {
                setUserSetting("hideTicketPopup", checked);
              }}
            />
          }
          label={t("Hide New Ticket Popup")}
          labelPlacement="start"
        />
      </FormControl>
      {!hideTicketPopup && (
        <>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              {t("Ticket Priority Popup Filter")}
            </FormLabel>
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                gap: 1,
                mt: 1.5,
                mx: 3
              }}>
              <AutoComplete
                fullWidth
                value={ticketFilterByPriority}
                options={ticketsPriorityFilter.map((values) => values.value)}
                label={t("Priority")}
                getOptionLabel={(option) =>
                  ticketsPriorityFilter.find(
                    (ticket) => ticket.value === option
                  )?.label || option
                }
                onChange={(e, values) => {
                  setUserSetting(
                    "ticketFilterByPriority",
                    values as Priority[]
                  );
                }}
                entries={ticketsPriorityFilter}
                renderInput={(params) => <TextField {...params} />}
                maxItem={4}
              />
            </Stack>
          </FormControl>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              {t("Ticket Popup Location")}
            </FormLabel>
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                gap: 1,
                mt: 1.5,
                mx: 3
              }}>
              <Select
                label={t("Popup Location")}
                value={popupLocation}
                onChange={(evt) => {
                  setUserSetting(
                    "popupLocation",
                    evt.target.value as PopupLocationKeys
                  );
                }}
              >
                {popupLocationValues.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(value)}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </FormControl>
        </>
      )}
      <GlobalListPageSizeField
        pageSize={pageSize}
        label={t("Display Rows per Page ({{min}}-{{max}})", {
          min: MIN_PAGE_SIZE,
          max: MAX_PAGE_SIZE,
        })}
        setPageSize={(value) => setGlobalListPreferences({ pageSize: value })}
      />
      <GlobalListPageSizeField
        pageSize={bulkImportPageSize}
        label={t("Import Rows per Page ({{min}}-{{max}})", {
          min: MIN_PAGE_SIZE,
          max: MAX_PAGE_SIZE,
        })}
        setPageSize={(value) =>
          setGlobalListPreferences({ bulkImportPageSize: value })
        }
      />
    </Stack>
  );
};

export default SettingsContent;
